import { lazy } from 'react';
import {
    mdiFinance, mdiGraph, mdiDolly, mdiWeb, mdiDumpTruck, mdiChartLine, mdiAccountDetails, mdiAccountGroup, mdiTableCheck
} from '@mdi/js';

import { RoleTypes } from '../helpers/enums';
import { AppRoles, RouteListItem } from '../interfaces/authorizationInterfaces';

// Market Insights
const MarketInsightsDashboard = lazy((): Promise<any> => { return import('../components/pages/marketInsights/dashboard'); });
const MarketInsightsLaneDetails = lazy((): Promise<any> => { return import('../components/pages/marketInsights/laneDetails'); });
const MarketInsightsDataCharts = lazy((): Promise<any> => { return import('../components/pages/marketInsights/dataCharts'); });
const MarketInsightsProfile = lazy((): Promise<any> => { return import('../components/pages/marketInsights/profile'); });

// Network Insights
const NetworkInsightsGeography = lazy((): Promise<any> => { return import('../components/pages/networkInsights/geography'); });
const NetworkInsightsShippers = lazy((): Promise<any> => { return import('../components/pages/networkInsights/shippers'); });

// Data Charts (aka Truck Load Rate)
const TruckLoadRateDataCharts = lazy((): Promise<any> => { return import('../components/pages/truckLoadRate/dataCharts'); });
const TruckLoadRateLaneDetails = lazy((): Promise<any> => { return import('../components/pages/truckLoadRate/laneDetails'); });
const TruckLoadRateRateManager = lazy((): Promise<any> => { return import('../components/pages/truckLoadRate/rateManager'); });
const TruckLoadRateAdmin = lazy((): Promise<any> => { return import('../components/pages/truckLoadRate/admin/admin'); });
const TruckLoadRateProfile = lazy((): Promise<any> => { return import('../components/pages/truckLoadRate/profile'); });

const marketInsightsBase = '/marketInsights';
const networkInsightsBase = '/networkInsights';
const truckLoadRateBase = '/truckLoadRate';

export const marketInsightsRoutes = {
    dashboard: `${marketInsightsBase}/dashboard`,
    laneDetails: `${marketInsightsBase}/laneDetails`,
    dataCharts: `${marketInsightsBase}/dataCharts`,
    profile: `${marketInsightsBase}/profile`
};

export const networkInsightsRoutes = {
    geography: `${networkInsightsBase}/geography`,
    shippers: `${networkInsightsBase}/shippers`
};

export const truckLoadRateRoutes = {
    dataCharts: `${truckLoadRateBase}/dataCharts`,
    laneDetails: `${truckLoadRateBase}/laneDetails`,
    rateManager: `${truckLoadRateBase}/rateManager`,
    admin: `${truckLoadRateBase}/admin`,
    profile: `${truckLoadRateBase}/profile`
};

export const publicRoutes = {
    root: '/',
    authorize: '/authorize',
    revoke: '/revoke',
    notAuthorized: '/notAuthorized',
    callback: '/callback'
};

const privateRouteList = (): RouteListItem[] => {
    return [
        {
            appTitle: 'Market Insights',
            component: MarketInsightsDashboard,
            path: marketInsightsRoutes.dashboard,
            menuTitle: 'Market Insights',
            menuIcon: mdiFinance,
            menuPath: marketInsightsRoutes.dashboard,
            allowedRoles: [RoleTypes.MarketInsights]
        },
        {
            appTitle: 'Market Insights',
            component: MarketInsightsDataCharts,
            path: marketInsightsRoutes.dataCharts,
            menuTitle: 'Data Charts',
            menuIcon: mdiChartLine,
            menuPath: marketInsightsRoutes.dataCharts,
            allowedRoles: [RoleTypes.MarketInsights]
        },
        {
            appTitle: 'Profile',
            component: MarketInsightsProfile,
            path: marketInsightsRoutes.profile,
            menuTitle: 'Profile',
            menuIcon: mdiAccountDetails,
            menuPath: marketInsightsRoutes.profile,
            allowedRoles: [RoleTypes.MarketInsights]
        },
        {
            appTitle: 'Market Insights',
            component: MarketInsightsLaneDetails,
            path: marketInsightsRoutes.laneDetails,
            allowedRoles: [RoleTypes.MarketInsights]
        },
        {
            appTitle: 'Network Insights',
            path: networkInsightsBase,
            menuTitle: 'Network Insights',
            menuIcon: mdiGraph,
            menuPath: networkInsightsBase,
            allowedRoles: [], // TODO: Add NI role back once this page is ready
            subRoutes: [
                {
                    appTitle: 'Network Insights',
                    component: NetworkInsightsGeography,
                    path: networkInsightsRoutes.geography,
                    menuTitle: 'Geography',
                    menuIcon: mdiWeb,
                    menuPath: networkInsightsRoutes.geography,
                    allowedRoles: [] // TODO: Add NI role back once this page is ready
                },
                {
                    appTitle: 'Network Insights',
                    component: NetworkInsightsShippers,
                    path: networkInsightsRoutes.shippers,
                    menuTitle: 'Shippers',
                    menuIcon: mdiDolly,
                    menuPath: networkInsightsRoutes.shippers,
                    allowedRoles: [] // TODO: Add NI role back once this page is ready
                }
            ]
        },
        {
            appTitle: 'TruckLoad Rates',
            component: TruckLoadRateDataCharts,
            path: truckLoadRateRoutes.dataCharts,
            menuTitle: 'TruckLoad Rates',
            menuIcon: mdiDumpTruck,
            menuPath: truckLoadRateRoutes.dataCharts,
            allowedRoles: [RoleTypes.TruckLoadRate]
        },
        {
            appTitle: 'TruckLoad Rates',
            component: TruckLoadRateRateManager,
            path: truckLoadRateRoutes.rateManager,
            menuTitle: 'Rate Manager',
            menuIcon: mdiTableCheck,
            menuPath: truckLoadRateRoutes.rateManager,
            allowedRoles: [RoleTypes.TruckLoadRate]
        },
        {
            appTitle: 'TruckLoad Rates',
            component: TruckLoadRateAdmin,
            path: truckLoadRateRoutes.admin,
            menuTitle: 'User Management',
            menuIcon: mdiAccountGroup,
            menuPath: truckLoadRateRoutes.admin,
            allowedRoles: [RoleTypes.TruckLoadRateAdmin]
        },
        {
            appTitle: 'TruckLoad Rates',
            component: TruckLoadRateProfile,
            path: truckLoadRateRoutes.profile,
            menuTitle: 'Profile',
            menuIcon: mdiAccountDetails,
            menuPath: truckLoadRateRoutes.profile,
            allowedRoles: [RoleTypes.TruckLoadRate]
        },
        {
            appTitle: 'TruckLoad Rates',
            component: TruckLoadRateLaneDetails,
            path: truckLoadRateRoutes.laneDetails,
            allowedRoles: [RoleTypes.TruckLoadRate]
        }
    ];
};

const allowedAppRoles = (appRoles: AppRoles): [string, boolean][] => {
    const roleEntries = Object.entries(appRoles);
    const authorizedAppRoles = roleEntries.filter((entry) => {
        const [, isAuthorized] = entry;
        return isAuthorized === true;
    });

    return authorizedAppRoles;
};

const isUserAuthorizedForRoute = (allowedRolesForUser: [string, boolean][], allowedRolesForRoute: RoleTypes[]): boolean => {
    // check the array of authorized roles for the route and see if the user is authorized for that route
    const isAuthorized = allowedRolesForUser.some(([roleName]) => {
        return allowedRolesForRoute.indexOf(roleName as RoleTypes) >= 0;
    });

    return isAuthorized;
};

/**
 * This will loop through all available routes and subroutes to determine if the user has access to it.
 * It will return just a flat list of authorized routes.
 * @param appRoles A list of AppRoles for the logged in user
 */
export const getAuthorizedRouteList = (appRoles: AppRoles): RouteListItem[] => {
    const allAuthorizedRoutes: RouteListItem[] = [];

    // first, get a list of authorized roles that the logged in user has access to
    const authorizedAppRoles = allowedAppRoles(appRoles);

    // get list of all available routes in the app
    const allAvailableRoutes = privateRouteList();

    // loop through all available routes and determine whether the user is authorized or not
    allAvailableRoutes.forEach((route: RouteListItem) => {
        const { allowedRoles } = route;

        if (isUserAuthorizedForRoute(authorizedAppRoles, allowedRoles) === true) {
            // if the user is authorized and there is a component property on the parent, add it to the list
            if (route.component) {
                allAuthorizedRoutes.push(route);
            }

            // check authorization for the subRoutes also
            const { subRoutes = [] } = route;
            subRoutes.forEach((subRoute: RouteListItem) => {
                const { allowedRoles: allowedSubRoles } = subRoute;

                // if the user is authorized and there is a component property on the child, add it to the list
                if (isUserAuthorizedForRoute(authorizedAppRoles, allowedSubRoles) === true && subRoute.component) {
                    allAuthorizedRoutes.push(subRoute);
                }
            });
        }
    });

    return allAuthorizedRoutes;
};

/**
 * This will check the authorized routes and return a list to render the menu.
 * NOTE: this will assume that if the user has access to the parent, they will have access to the subRoute children.
 * @param appRoles A list of AppRoles for the logged in user
 */
export const getAuthorizedMenuList = (appRoles: AppRoles): RouteListItem[] => {
    const allAuthorizedMenuItems: RouteListItem[] = [];

    // first, get a list of authorized roles that the logged in user has access to
    const authorizedAppRoles = allowedAppRoles(appRoles);

    // get list of all available routes in the app
    const allAvailableRoutes = privateRouteList();

    // loop through all available routes and determine whether the user is authorized or not
    allAvailableRoutes.forEach((route: RouteListItem) => {
        const { allowedRoles } = route;

        // if the route is authorized and has a menuIcon, add it to the list
        // NOTE: adding only allowed subRoutes based on allowed roles.
        if (isUserAuthorizedForRoute(authorizedAppRoles, allowedRoles) === true && route.menuIcon) {
            const { subRoutes = [] } = route;
            const allowedSubRoutes: RouteListItem[] = [];
            subRoutes.forEach((subRoute: RouteListItem) => {
                const { allowedRoles: allowedSubRoles } = subRoute;
                if (isUserAuthorizedForRoute(authorizedAppRoles, allowedSubRoles) === true && subRoute.component) {
                    allowedSubRoutes.push(subRoute);
                }
            });

            const filteredRoute = { ...route, subRoutes: allowedSubRoutes };
            allAuthorizedMenuItems.push(filteredRoute);
        }
    });

    return allAuthorizedMenuItems;
};
